export type LandingLocale = 'en' | 'ar' | 'tr' | 'fr'

export type LandingCta = {
  label: string
  href: string
}

export type LandingHeroContent = {
  eyebrow: string
  title: string
  copy: string
  primaryCta: LandingCta
  secondaryCta: LandingCta
  proof: string[]
}

export type ProblemSolutionContent = {
  eyebrow: string
  problemTitle: string
  problemCopy: string
  solutionTitle: string
  solutionCopy: string
}

export type ComparisonContent = {
  eyebrow: string
  title: string
  oldWayTitle: string
  newWayTitle: string
  oldWay: string[]
  newWay: string[]
}

export type FeatureCardContent = {
  title: string
  copy: string
}

export type FaqEntry = {
  question: string
  answer: string
}

export type FooterContent = {
  copyright: string
  links: LandingCta[]
}

export type PricingBand = {
  label: string
  price: string
  capacity?: string
}

export type PricingOverage = {
  label: string
  value: string
}

export type PricingExample = {
  exampleKey: string
  name: string
  math: string[]
  total: string
  annual: string
}

export type PricingContent = {
  eyebrow: string
  title: string
  subtitle: string
  promo?: string
  locationNote: string
  includedLabel: string
  bands: PricingBand[]
  examples: PricingExample[]
  features: string[]
  overageTitle?: string
  overages?: PricingOverage[]
  contactCta?: LandingCta
  implementationNote?: string
}

export type RoiCalculatorContent = {
  eyebrow: string
  title: string
  subtitle: string
  inputs: {
    workers: string
    locations: string
    adminHours: string
    adminRate: string
    supervisorHours: string
  }
  results: {
    weekly: string
    monthly: string
    annual: string
    reclaimed: string
  }
  assumptionsLabel: string
  formula: string
  note: string
  cta: LandingCta
  linksTitle: string
  links: LandingCta[]
}

export type NexusLandingContent = {
  nav: string[]
  signIn: string
  hero: LandingHeroContent
  roi: RoiCalculatorContent
  problemSolution: ProblemSolutionContent
  comparison: ComparisonContent
  pricing: PricingContent
  featuresTitle: string
  features: FeatureCardContent[]
  useCasesTitle: string
  useCases: FeatureCardContent[]
  faqTitle: string
  faqEyebrow: string
  faq: FaqEntry[]
  finalTitle: string
  finalCopy: string
  finalCta: LandingCta
  footer: FooterContent
}

const faq: FaqEntry[] = [
  {
    question: 'Can a worker punch in outside the approved zone?',
    answer: 'NexusWorkforce360 can enforce geofence rules around approved work locations. If a worker attempts to punch in outside the configured radius, the system can block the punch or flag it for review depending on the organization policy. Supervisors get clearer visibility instead of discovering issues later in payroll.',
  },
  {
    question: 'How do offline punches work?',
    answer: 'Workers can record punch activity when connectivity is poor. Offline punch events are stored locally and synced once the device reconnects. NexusWorkforce360 keeps audit details so managers can review timing, location context, and sync status before approval.',
  },
  {
    question: 'How are hour disputes handled?',
    answer: 'NexusWorkforce360 supports structured timesheet review, comments, adjustments, rejection, dispute handling, and approval history. Instead of resolving payroll issues through scattered emails or WhatsApp messages, disputes stay attached to the relevant shift and worker record.',
  },
  {
    question: 'How do ratings or reliability scores work?',
    answer: 'NexusWorkforce360 can track reliability indicators such as attendance history, punctuality, completed assignments, break compliance, and supervisor reviews. These signals help managers make better assignment decisions without relying only on memory or informal feedback.',
  },
  {
    question: 'Can agencies and companies see different information?',
    answer: 'Yes. NexusWorkforce360 is built around tenant, organization, and scoped access controls. Agencies, companies, supervisors, and platform admins can be given different visibility depending on their role, organization, location, or assignment scope.',
  },
  {
    question: 'Can NexusWorkforce360 manage both internal employees and external agency workers?',
    answer: 'Yes. That is the core positioning. NexusWorkforce360 helps organizations manage direct employees, agency workers, subcontractors, temporary labor, and project-based field teams under one operational umbrella.',
  },
  {
    question: 'Can different agencies use the same platform without seeing each other private data?',
    answer: 'Yes. NexusWorkforce360 presents agency and company relationships through controlled permissions and sharing rules. Each organization sees only the workers, jobs, documents, timesheets, and approvals they are authorized to access.',
  },
  {
    question: 'Does NexusWorkforce360 replace staffing agencies?',
    answer: 'No. NexusWorkforce360 is designed to help companies and agencies work together more efficiently. It standardizes attendance, assignments, compliance, approvals, and reporting while allowing agencies to continue supplying and managing their workforce.',
  },
  {
    question: 'What problems does NexusWorkforce360 solve compared to spreadsheets and email?',
    answer: 'NexusWorkforce360 reduces fragmented worker lists, manual timesheet reconciliation, agency-specific templates, missing compliance documents, approval delays, and payroll disputes by creating one source of truth across internal and external workforce operations.',
  },
  {
    question: 'What industries is NexusWorkforce360 best suited for?',
    answer: 'NexusWorkforce360 is especially suited for construction, facilities management, logistics, hospitality, events, security, cleaning, staffing, manpower supply, and any operation managing workers across multiple locations, agencies, or subcontractors.',
  },
  {
    question: 'Can NexusWorkforce360 support payroll exports?',
    answer: 'Yes. NexusWorkforce360 can generate structured reporting and payroll export data using filters such as date range, worker, location, agency, company, job, assignment, approval state, and export state.',
  },
  {
    question: 'Can supervisors approve timesheets?',
    answer: 'Yes. NexusWorkforce360 supports supervisor and company approval workflows with audit history, comments, disputes, and adjustments so timesheets can move from field review to export-ready data.',
  },
  {
    question: 'Can worker documents and certifications be tracked?',
    answer: 'Yes. NexusWorkforce360 can track documents, certifications, expiry risks, verification status, and job-specific credential requirements. This is especially useful for regulated, site-based, or contractor-heavy environments.',
  },
  {
    question: 'Is NexusWorkforce360 only a time-tracking app?',
    answer: 'No. Time tracking, geofencing, and attendance are important features, but the larger value is workforce network management. NexusWorkforce360 helps organizations coordinate employees, agencies, subcontractors, temporary workers, approvals, documents, assignments, and reporting in one platform.',
  },
  {
    question: 'Why is NexusWorkforce360 different from standard attendance apps?',
    answer: 'Most attendance apps focus on a single employer and its direct employees. NexusWorkforce360 is designed for multi-organization workforce operations where companies, agencies, subcontractors, supervisors, and workers all need controlled access to the same operational flow.',
  },
]

const arabicFaq: FaqEntry[] = [
  {
    question: 'هل يمكن للعامل تسجيل الحضور خارج النطاق المعتمد؟',
    answer: 'يمكن لـ رقيب 360 فرض قواعد النطاق الجغرافي حول مواقع العمل المعتمدة. إذا حاول العامل التسجيل خارج نصف القطر المحدد، يمكن للنظام منع التسجيل أو وضعه للمراجعة حسب سياسة المؤسسة.',
  },
  {
    question: 'كيف تعمل التسجيلات دون اتصال؟',
    answer: 'يمكن للعمال تسجيل نشاط الحضور عند ضعف الاتصال. تحفظ أحداث التسجيل محليا وتتم مزامنتها عند عودة الاتصال، مع تفاصيل مراجعة تساعد المديرين على فحص الوقت والموقع وحالة المزامنة.',
  },
  {
    question: 'كيف تتم معالجة نزاعات الساعات؟',
    answer: 'يدعم رقيب 360 مراجعة الجداول والتعليقات والتعديلات والرفض والنزاعات وسجل الاعتماد، بحيث تبقى مشكلة الرواتب مرتبطة بالمناوبة والعامل بدلا من رسائل متفرقة.',
  },
  {
    question: 'كيف تعمل التقييمات أو مؤشرات الموثوقية؟',
    answer: 'يمكن تتبع مؤشرات مثل سجل الحضور والالتزام بالمواعيد والمهام المكتملة والالتزام بالاستراحات ومراجعات المشرفين، لمساعدة المديرين على اتخاذ قرارات تعيين أفضل.',
  },
  {
    question: 'هل ترى الوكالات والشركات معلومات مختلفة؟',
    answer: 'نعم. يعتمد رقيب 360 على ضوابط المستأجر والمؤسسة والصلاحيات المحددة، بحيث تختلف الرؤية حسب الدور والمؤسسة والموقع ونطاق التعيين.',
  },
  {
    question: 'هل يدير رقيب 360 الموظفين الداخليين وعمال الوكالات؟',
    answer: 'نعم. يساعد النظام المؤسسات على إدارة الموظفين المباشرين وعمال الوكالات والمقاولين والعمال المؤقتين والفرق الميدانية في مظلة تشغيلية واحدة.',
  },
  {
    question: 'هل يمكن لعدة وكالات استخدام المنصة دون رؤية بيانات بعضها؟',
    answer: 'نعم. تعرض المنصة علاقات الوكالة والشركة من خلال صلاحيات وقواعد مشاركة مضبوطة، وترى كل مؤسسة فقط ما يسمح لها به من عمال ووظائف ومستندات وجداول واعتمادات.',
  },
  {
    question: 'هل يستبدل رقيب 360 وكالات التوظيف؟',
    answer: 'لا. صمم رقيب 360 لمساعدة الشركات والوكالات على العمل معا بكفاءة أكبر عبر توحيد الحضور والتعيينات والامتثال والاعتمادات والتقارير.',
  },
  {
    question: 'ما الفرق مقارنة بالجداول والبريد الإلكتروني؟',
    answer: 'يقلل رقيب 360 القوائم المتفرقة والمطابقة اليدوية وتأخر الاعتمادات ونزاعات الرواتب عبر إنشاء مصدر حقيقة واحد للعمليات الداخلية والخارجية.',
  },
  {
    question: 'ما القطاعات الأنسب لـ رقيب 360؟',
    answer: 'يناسب المقاولات وإدارة المرافق واللوجستيات والضيافة والفعاليات والأمن والتنظيف والتوظيف وتوريد العمالة وأي تشغيل يدير عمالا عبر مواقع أو وكالات متعددة.',
  },
  {
    question: 'هل يدعم رقيب 360 تصديرات الرواتب؟',
    answer: 'نعم. يمكن إنشاء بيانات تقارير وتصدير منظمة باستخدام فلاتر التاريخ والعامل والموقع والوكالة والشركة والوظيفة وحالة الاعتماد والتصدير.',
  },
  {
    question: 'هل يستطيع المشرفون اعتماد الجداول الزمنية؟',
    answer: 'نعم. يدعم النظام اعتماد المشرف والشركة مع سجل تدقيق وتعليقات ونزاعات وتعديلات حتى تنتقل الجداول من مراجعة الميدان إلى بيانات جاهزة للتصدير.',
  },
  {
    question: 'هل يمكن تتبع مستندات وشهادات العمال؟',
    answer: 'نعم. يمكن تتبع المستندات والشهادات ومخاطر الانتهاء وحالة التحقق ومتطلبات الاعتماد حسب الوظيفة.',
  },
  {
    question: 'هل رقيب 360 مجرد تطبيق لتتبع الوقت؟',
    answer: 'لا. تتبع الوقت والنطاق الجغرافي مهمان، لكن القيمة الأكبر هي إدارة شبكة القوى العاملة عبر الموظفين والوكالات والمقاولين والاعتمادات والمستندات والتقارير.',
  },
  {
    question: 'لماذا يختلف عن تطبيقات الحضور العادية؟',
    answer: 'تركز معظم تطبيقات الحضور على صاحب عمل واحد. أما رقيب 360 فمصمم لعمليات متعددة المؤسسات تحتاج فيها الشركات والوكالات والمقاولون والمشرفون والعمال إلى وصول مضبوط لنفس سير العمل.',
  },
]

const frenchFaq: FaqEntry[] = [
  {
    question: 'Un travailleur peut-il pointer hors de la zone approuvée ?',
    answer: 'NexusWorkforce360 peut appliquer des règles de géofence autour des sites approuvés. Si un travailleur tente de pointer hors du rayon configuré, le système peut bloquer le pointage ou le signaler pour revue selon la politique.',
  },
  {
    question: 'Comment fonctionnent les pointages hors ligne ?',
    answer: 'Les travailleurs peuvent enregistrer des pointages lorsque la connexion est faible. Les événements sont stockés localement puis synchronisés au retour du réseau, avec contexte temps, lieu et statut de synchronisation.',
  },
  {
    question: 'Comment les litiges d’heures sont-ils gérés ?',
    answer: 'NexusWorkforce360 prend en charge revue des feuilles de temps, commentaires, ajustements, rejets, litiges et historique d’approbation afin que les problèmes restent liés au shift et au travailleur concernés.',
  },
  {
    question: 'Comment fonctionnent les scores de fiabilité ?',
    answer: 'La plateforme peut suivre présence, ponctualité, affectations terminées, respect des pauses et revues superviseur pour aider les managers à affecter les bonnes personnes.',
  },
  {
    question: 'Les agences et entreprises voient-elles des informations différentes ?',
    answer: 'Oui. Les contrôles tenant, organisation et accès limité permettent de donner une visibilité différente selon le rôle, l’organisation, le site ou l’affectation.',
  },
  {
    question: 'La plateforme gère-t-elle employés internes et travailleurs agence ?',
    answer: 'Oui. NexusWorkforce360 aide à gérer employés directs, travailleurs agence, sous-traitants, temporaires et équipes terrain dans une même vue opérationnelle.',
  },
  {
    question: 'Plusieurs agences peuvent-elles utiliser la plateforme sans voir les données privées des autres ?',
    answer: 'Oui. Chaque organisation voit uniquement les travailleurs, jobs, documents, feuilles de temps et validations auxquels elle est autorisée.',
  },
  {
    question: 'NexusWorkforce360 remplace-t-il les agences de staffing ?',
    answer: 'Non. Il aide les entreprises et agences à collaborer plus efficacement en standardisant présence, affectations, conformité, validations et reporting.',
  },
  {
    question: 'Quels problèmes sont résolus par rapport aux tableurs et e-mails ?',
    answer: 'La plateforme réduit listes fragmentées, rapprochement manuel, modèles par agence, documents manquants, retards d’approbation et litiges paie en créant une source de vérité partagée.',
  },
  {
    question: 'Pour quels secteurs NexusWorkforce360 est-il adapté ?',
    answer: 'Il convient particulièrement à construction, facilities, logistique, hospitality, événements, sécurité, nettoyage, staffing, manpower supply et opérations multi-sites.',
  },
  {
    question: 'NexusWorkforce360 prend-il en charge les exports paie ?',
    answer: 'Oui. Il peut générer des données d’export et de reporting filtrées par date, travailleur, site, agence, entreprise, job, affectation et statut.',
  },
  {
    question: 'Les superviseurs peuvent-ils valider les feuilles de temps ?',
    answer: 'Oui. Les workflows superviseur et entreprise incluent historique, commentaires, litiges et ajustements pour préparer des données prêtes à l’export.',
  },
  {
    question: 'Les documents et certifications travailleurs sont-ils suivis ?',
    answer: 'Oui. Documents, certifications, risques d’expiration, statut de vérification et exigences par job peuvent être suivis.',
  },
  {
    question: 'Est-ce seulement une application de suivi du temps ?',
    answer: 'Non. Le suivi du temps et la géofence sont importants, mais la valeur principale est la gestion du réseau workforce entre employés, agences, sous-traitants, validations, documents et reporting.',
  },
  {
    question: 'Pourquoi est-ce différent des applications de présence standard ?',
    answer: 'La plupart des outils ciblent un employeur unique. NexusWorkforce360 est conçu pour les opérations multi-organisations où entreprises, agences, sous-traitants, superviseurs et travailleurs partagent un flux contrôlé.',
  },
]

const pricingContent: PricingContent = {
  eyebrow: 'NexusWorkforce360 pricing',
  title: 'Start free. Scale by capacity.',
  subtitle: 'Begin on Free with 10 users and one supervisor/admin, including six months of full access to eligible core workforce capabilities. Move to Starter, Growth, or Operations as your workforce grows. Punch AI, Predictive Forecasting, Punch Learning, and Punch Mentor are separately priced.',
  promo: 'Free: 10 users + 1 supervisor. Six months of full core access, then Basic mode. No credit card required.',
  locationNote: 'Worker and location overages apply above each plan’s included capacity. Fair-use and security protections always apply.',
  includedLabel: 'Every plan includes:',
  bands: [
    { label: 'Free', price: '$0', capacity: '10 users + 1 supervisor · 6-month full-core promotion · Basic mode after' },
    { label: 'Starter', price: '$1,000/mo', capacity: '100 workers · 2 locations · core workforce operations' },
    { label: 'Growth', price: '$3,000/mo', capacity: '300 workers · 5 locations · enhanced workflows and reporting' },
    { label: 'Operations', price: '$8,000/mo', capacity: '1,000 workers · 15 locations · advanced governance and audit' },
  ],
  overageTitle: 'Overages and separately priced products',
  overages: [
    { label: 'Additional active worker', value: '$10 (Starter) · $9 (Growth) · $8 (Operations) / worker / month' },
    { label: 'Additional location', value: '$150 / location / month' },
    { label: 'Punch AI', value: 'From $399 / month (configured allowance)' },
    { label: 'Predictive Forecasting', value: 'From $799 / month · 5 sites included, then $75 / site' },
    { label: 'Punch Learning', value: '$6 / active learner / month · $150 minimum' },
    { label: 'Punch Mentor', value: '$12 / mentored learner / month · $250 minimum' },
    { label: 'Implementation', value: 'One-time, from $2,500 (Starter) to $10,000 (Operations)' },
  ],
  contactCta: { label: 'Contact Sales', href: '#contact' },
  implementationNote: 'Enterprise and Self-hosted deployments are available as custom-quoted arrangements. Premium products (Punch AI, Predictive Forecasting, Punch Learning, Punch Mentor) are never bundled into a plan and are purchased separately. Implementation, migration, and training are optional one-time services.',
  examples: [
    {
      exampleKey: 'small',
      name: 'Free',
      math: ['$0 — 10 users + 1 supervisor/admin', 'Six months of full eligible core access', 'Automatically continues in Basic mode (no deletion)'],
      total: '$0',
      annual: 'No credit card required. Premium products sold separately.',
    },
    {
      exampleKey: 'mid-size',
      name: 'Starter',
      math: ['$1,000 / month base', '100 workers · 2 locations included', '+$10 / extra worker · +$150 / extra location'],
      total: '$1,000/mo',
      annual: 'Core operations, scheduling, geofencing, timesheets, payroll-ready exports.',
    },
    {
      exampleKey: 'professional',
      name: 'Growth',
      math: ['$3,000 / month base', '300 workers · 5 locations included', '+$9 / extra worker · +$150 / extra location'],
      total: '$3,000/mo',
      annual: 'Everything in Starter plus enhanced cross-organization workflows and reporting.',
    },
    {
      exampleKey: 'larger',
      name: 'Operations',
      math: ['$8,000 / month base', '1,000 workers · 15 locations included', '+$8 / extra worker · +$150 / extra location'],
      total: '$8,000/mo',
      annual: 'Everything in Growth plus advanced governance, audit, and priority service.',
    },
  ],
  features: [
    'Scheduling',
    'Mobile attendance',
    'Geofencing',
    'Timesheet approvals',
    'Worker documents',
    'Payroll exports',
    'Reporting',
    'Workforce relationship management',
  ],
}

const englishContent: NexusLandingContent = {
  nav: ['Free', 'Network OS', 'Compare', 'Pricing', 'Screens', 'Contact'],
  signIn: 'Sign in',
  hero: {
    eyebrow: 'The Free Workforce Network OS',
    title: 'Run Your Workforce for Free',
    copy: 'Manage workers, jobs, schedules, locations, time tracking, geofencing, compliance, timesheets, and approvals on the $0 core platform. Companies and staffing agencies participate in the same Workforce Network OS, with no per-worker or per-location pricing for core operations.',
    primaryCta: { label: 'Start Free', href: '/login?mode=register' },
    secondaryCta: { label: 'See How It Works', href: '#product' },
    proof: ['$0 core workforce operations', 'No per-worker or per-location pricing', 'Companies, staffing agencies, and workers connected'],
  },
  roi: {
    eyebrow: 'Planning estimate',
    title: 'Estimate your hidden workforce coordination cost',
    subtitle: 'Manual scheduling, WhatsApp follow-ups, paper timesheets, attendance checks, and payroll corrections quietly consume hours every week.',
    inputs: {
      workers: 'Active workers',
      locations: 'Locations/sites',
      adminHours: 'Admin hours per week',
      adminRate: 'Average admin hourly cost',
      supervisorHours: 'Supervisor validation hours per week',
    },
    results: {
      weekly: 'Weekly admin coordination cost',
      monthly: 'Monthly coordination cost',
      annual: 'Annual coordination cost',
      reclaimed: 'Potential annual time reclaimed at 25% reduction',
    },
    assumptionsLabel: 'Assumptions',
    formula: 'Formula: (admin hours + supervisor validation hours) x hourly cost. Monthly uses 4.33 weeks; annual uses 52 weeks.',
    note: 'Estimates are based on user-entered assumptions and are for planning purposes only.',
    cta: { label: 'See how NexusWorkforce360 reduces coordination work', href: '#contact' },
    linksTitle: 'Related workforce operations pages',
    links: [
      { label: 'Construction workforce management software', href: '/construction-workforce-management-software' },
      { label: 'Staffing agency timesheet software', href: '/staffing-agency-timesheet-software' },
      { label: 'Warehouse staffing agency software', href: '/warehouse-staffing-agency-software' },
      { label: 'Construction workforce compliance software', href: '/construction-workforce-compliance-software' },
    ],
  },
  problemSolution: {
    eyebrow: 'Problem and solution',
    problemTitle: 'Manual validation turns every payroll close into back-office friction.',
    problemCopy: 'Agencies reconcile schedules, worker hours, timesheets, rate sheets, client approvals, invoice questions, and payroll prep across chats and spreadsheets. Companies spend time approving hours, validating invoices, resolving attendance disputes, and checking labor across locations.',
    solutionTitle: 'NexusWorkforce360 standardizes agency and client validation around the same verified data.',
    solutionCopy: 'Start with your agency operations, then invite client companies into simple approvals and billing visibility. The same assignment, attendance, compliance, timesheet, and export record supports agency billing and company approval without rebuilding the truth twice.',
  },
  comparison: {
    eyebrow: 'Old way vs NexusWorkforce360',
    title: 'Replace supplier-by-supplier chaos with one shared approval and billing workflow.',
    oldWayTitle: 'Old way',
    newWayTitle: 'NexusWorkforce360 way',
    oldWay: [
      'Agency-specific spreadsheets',
      'Timesheets by email',
      'WhatsApp approvals',
      'Manual invoice reconciliation',
      'Payroll prep rebuilt from disconnected files',
      'Separate worker lists',
      'Limited visibility across sites',
      'Compliance documents scattered across folders',
      'Different process for every supplier',
    ],
    newWay: [
      'Unified workforce dashboard',
      'Standardized attendance and timesheets',
      'Structured approval workflows',
      'Agency/company visibility controls',
      'Shared billing validation before invoice disputes',
      'Worker assignment history',
      'Document and certification readiness',
      'Export-ready payroll and reporting data',
      'One process across internal and external workforce',
    ],
  },
  pricing: pricingContent,
  featuresTitle: 'Sales-ready workforce platform capabilities',
  features: [
    { title: 'Administrative Time Savings', copy: 'Save 4-5 hours per week by replacing manual timesheet validation, billing checks, approval chasing, and payroll prep with one workflow.' },
    { title: 'Agency-Led Client Collaboration', copy: 'Start inside the agency team, then invite client companies into scoped approvals and billing visibility when the workflow is ready.' },
    { title: 'Workforce Network Management', copy: 'Manage employees, agencies, subcontractors, and external labor suppliers from one platform.' },
    { title: 'Attendance & Geofencing', copy: 'Track site attendance, offline punches, location compliance, breaks, and exceptions.' },
    { title: 'Scheduling & Assignments', copy: 'Create jobs, shifts, assignments, open shifts, and worker applications across locations and organizations.' },
    { title: 'Compliance & Documents', copy: 'Track worker documents, certifications, expiry risks, and job-specific credential requirements.' },
    { title: 'Timesheets, Approvals & Billing Validation', copy: 'Standardize supervisor and company approvals with audit history, comments, disputes, adjustments, and cleaner invoice support.' },
    { title: 'Payroll & Reporting Exports', copy: 'Generate clean export-ready data by worker, agency, company, location, job, approval state, and date range.' },
    { title: 'Agency & Subcontractor Visibility', copy: 'Give each organization the right level of access without exposing everything across tenants.' },
    { title: 'Skills Progression & Reliability', copy: 'Track skills, identify gaps, match qualified workers, and support advancement paths alongside reliability and attendance signals.' },
  ],
  useCasesTitle: 'Built for operations that rely on many labor sources',
  useCases: [
    { title: 'Construction and project sites', copy: 'Coordinate direct labor, subcontractors, site assignments, compliance documents, attendance, approvals, and exports.' },
    { title: 'Facilities management', copy: 'Standardize coverage, mobile attendance, supervisor review, and timesheet close across distributed properties.' },
    { title: 'Logistics and delivery operations', copy: 'Manage field teams, temporary labor, location coverage, shift exceptions, and export-ready reporting.' },
    { title: 'Hospitality and events', copy: 'Bring agencies, temporary workers, supervisors, schedules, and approvals into one event-ready process.' },
    { title: 'Security and cleaning contractors', copy: 'Track post coverage, location attendance, worker documents, and supervisor approvals by client site.' },
    { title: 'Staffing and manpower agencies', copy: 'Launch with agency operations first, then bring clients into shared approvals, billing validation, and workforce visibility.' },
    { title: 'Multi-location operations', copy: 'Give leaders visibility across locations, projects, agencies, workers, timesheets, compliance, approvals, and exports.' },
  ],
  faqTitle: 'Sales questions executives and operators ask',
  faqEyebrow: 'FAQ',
  faq,
  finalTitle: 'Reduce workforce administration by 260+ hours per year.',
  finalCopy: 'Bring schedules, attendance, approvals, timesheets, billing validation, compliance readiness, skills progression, and payroll exports into one agency-client operating record.',
  finalCta: { label: 'Start Free', href: '/login?mode=register' },
  footer: {
    copyright: '© 2026 NexusWorkforce360. All rights reserved.',
    links: [
      { label: 'Platform', href: '#platform' },
      { label: 'Use Cases', href: '#use-cases' },
      { label: 'Contact', href: '#contact' },
    ],
  },
}

const arabicPricingContent: PricingContent = {
  eyebrow: 'تسعير NexusWorkforce360',
  title: 'ابدأ مجاناً. ثم توسّع حسب السعة.',
  subtitle: 'ابدأ على الخطة المجانية مع 10 مستخدمين ومشرف/مدير واحد، وتشمل ستة أشهر من الوصول الكامل إلى قدرات القوى العاملة الأساسية المؤهلة. انتقل إلى Starter أو Growth أو Operations مع نمو قوتك العاملة. تُسعَّر Punch AI و Predictive Forecasting و Punch Learning و Punch Mentor بشكل منفصل.',
  promo: 'المجانية: 10 مستخدمين + مشرف واحد. ستة أشهر من الوصول الكامل للأساسيات، ثم وضع Basic. دون بطاقة ائتمان.',
  locationNote: 'تُطبَّق رسوم تجاوز لكل عامل ولكل موقع فوق السعة المضمّنة في كل خطة. تبقى ضوابط الاستخدام العادل والأمان مطبقة.',
  includedLabel: 'تشمل كل خطة:',
  bands: [
    { label: 'مجاني', price: '0 دولار', capacity: '10 مستخدمين + مشرف واحد · ترويج أساسي 6 أشهر · ثم وضع Basic' },
    { label: 'Starter', price: '1,000 دولار/شهر', capacity: '100 عامل · موقعان · عمليات القوى العاملة الأساسية' },
    { label: 'Growth', price: '3,000 دولار/شهر', capacity: '300 عامل · 5 مواقع · تدفقات وتقارير محسّنة' },
    { label: 'Operations', price: '8,000 دولار/شهر', capacity: '1,000 عامل · 15 موقعاً · حوكمة وتدقيق متقدمان' },
  ],
  overageTitle: 'التجاوزات والمنتجات المُسعّرة بشكل منفصل',
  overages: [
    { label: 'عامل نشط إضافي', value: '10 دولارات (Starter) · 9 (Growth) · 8 (Operations) لكل عامل/شهر' },
    { label: 'موقع إضافي', value: '150 دولاراً لكل موقع/شهر' },
    { label: 'Punch AI', value: 'ابتداءً من 399 دولاراً/شهر (حصة مُهيّأة)' },
    { label: 'Predictive Forecasting', value: 'ابتداءً من 799 دولاراً/شهر · 5 مواقع مضمّنة ثم 75 دولاراً/موقع' },
    { label: 'Punch Learning', value: '6 دولارات لكل متعلّم نشط/شهر · حد أدنى 150 دولاراً' },
    { label: 'Punch Mentor', value: '12 دولاراً لكل متعلّم مُوجَّه/شهر · حد أدنى 250 دولاراً' },
    { label: 'التنفيذ', value: 'رسوم لمرة واحدة، من 2,500 دولار (Starter) إلى 10,000 دولار (Operations)' },
  ],
  contactCta: { label: 'تواصل مع المبيعات', href: '#contact' },
  implementationNote: 'تتوفر عمليات نشر Enterprise والاستضافة الذاتية كترتيبات بعرض سعر مخصّص. المنتجات المميزة (Punch AI و Predictive Forecasting و Punch Learning و Punch Mentor) لا تُضمَّن أبداً في أي خطة وتُشترى بشكل منفصل. التنفيذ والترحيل والتدريب خدمات اختيارية لمرة واحدة.',
  examples: [
    { exampleKey: 'small', name: 'مجاني', math: ['0 دولار — 10 مستخدمين + مشرف/مدير واحد', 'ستة أشهر من الوصول الكامل للأساسيات المؤهلة', 'يستمر تلقائياً في وضع Basic (دون حذف)'], total: '0 دولار', annual: 'دون بطاقة ائتمان. المنتجات المميزة تُباع منفصلة.' },
    { exampleKey: 'mid-size', name: 'Starter', math: ['1,000 دولار/شهر أساسي', '100 عامل · موقعان مضمّنان', '+10 دولارات/عامل · +150 دولاراً/موقع'], total: '1,000 دولار/شهر', annual: 'عمليات أساسية وجدولة ونطاق جغرافي وجداول وقت وتصديرات رواتب.' },
    { exampleKey: 'professional', name: 'Growth', math: ['3,000 دولار/شهر أساسي', '300 عامل · 5 مواقع مضمّنة', '+9 دولارات/عامل · +150 دولاراً/موقع'], total: '3,000 دولار/شهر', annual: 'كل ما في Starter بالإضافة إلى تدفقات بين المؤسسات وتقارير محسّنة.' },
    { exampleKey: 'larger', name: 'Operations', math: ['8,000 دولار/شهر أساسي', '1,000 عامل · 15 موقعاً مضمّناً', '+8 دولارات/عامل · +150 دولاراً/موقع'], total: '8,000 دولار/شهر', annual: 'كل ما في Growth بالإضافة إلى حوكمة وتدقيق متقدمين وخدمة ذات أولوية.' },
  ],
  features: [
    'الجدولة',
    'الحضور عبر الهاتف',
    'النطاق الجغرافي',
    'اعتماد جداول الوقت',
    'مستندات العمال',
    'تصديرات الرواتب',
    'التقارير',
    'إدارة علاقات القوى العاملة',
  ],
}

const arabicContent: NexusLandingContent = {
  ...englishContent,
  nav: ['التوفير', 'نظام الشبكة', 'المقارنة', 'التسعير', 'الشاشات', 'التواصل'],
  signIn: 'تسجيل الدخول',
  hero: {
    eyebrow: 'نظام التشغيل المجاني لشبكة القوى العاملة',
    title: 'شغّل قوتك العاملة مجاناً',
    copy: 'أدر العمال والوظائف والجداول والمواقع وتتبع الوقت والنطاق الجغرافي والامتثال وجداول الساعات والاعتمادات ضمن المنصة الأساسية بقيمة 0 دولار. تعمل الشركات ووكالات التوظيف في شبكة واحدة دون تسعير لكل عامل أو موقع للعمليات الأساسية.',
    primaryCta: { label: 'ابدأ مجاناً', href: '/login?mode=register' },
    secondaryCta: { label: 'شاهد كيف تعمل', href: '#product' },
    proof: ['عمليات أساسية بقيمة 0 دولار', 'لا تسعير لكل عامل أو موقع', 'ربط الشركات والوكالات والعمال'],
  },
  roi: {
    eyebrow: 'تقدير للتخطيط',
    title: 'قدّر التكلفة الخفية لتنسيق القوى العاملة',
    subtitle: 'تستهلك الجدولة اليدوية، ومتابعات واتساب، وجداول الوقت الورقية، وفحوص الحضور، وتصحيحات الرواتب ساعات هادئة كل أسبوع.',
    inputs: {
      workers: 'العمال النشطون',
      locations: 'المواقع',
      adminHours: 'ساعات الإدارة أسبوعيا',
      adminRate: 'متوسط تكلفة ساعة الإدارة',
      supervisorHours: 'ساعات تحقق المشرف أسبوعيا',
    },
    results: {
      weekly: 'تكلفة التنسيق الإداري الأسبوعية',
      monthly: 'تكلفة التنسيق الشهرية',
      annual: 'تكلفة التنسيق السنوية',
      reclaimed: 'وقت سنوي محتمل يمكن استعادته عند خفض 25%',
    },
    assumptionsLabel: 'الافتراضات',
    formula: 'الصيغة: (ساعات الإدارة + ساعات تحقق المشرف) × تكلفة الساعة. الشهر يستخدم 4.33 أسابيع، والسنة تستخدم 52 أسبوعا.',
    note: 'تعتمد التقديرات على افتراضات يدخلها المستخدم وهي لأغراض التخطيط فقط.',
    cta: { label: 'ناقش احتياجات القوى العاملة', href: '#contact' },
    linksTitle: 'صفحات ذات صلة بعمليات القوى العاملة',
    links: [
      { label: 'برنامج إدارة العمالة في مواقع البناء', href: '/ar/construction-workforce-management-software' },
      { label: 'برنامج إدارة شركات التوظيف وتوريد العمالة', href: '/ar/staffing-agency-software' },
      { label: 'نظام حضور وانصراف بالموقع الجغرافي', href: '/ar/geofenced-attendance-tracking' },
      { label: 'برنامج إدارة مستندات العمال', href: '/ar/worker-document-compliance' },
    ],
  },
  problemSolution: {
    eyebrow: 'المشكلة والحل',
    problemTitle: 'التحقق اليدوي يحول كل إغلاق رواتب إلى احتكاك إداري.',
    problemCopy: 'تطابق الوكالات الجداول وساعات العمال وجداول الوقت والأسعار واعتمادات العملاء وأسئلة الفواتير وتحضير الرواتب عبر محادثات وجداول متفرقة. وتقضي الشركات وقتا في اعتماد الساعات والتحقق من الفواتير وحل نزاعات الحضور وتتبع العمالة عبر المواقع.',
    solutionTitle: 'يوحد رقيب 360 الشركة والوكالة والمقاول حول السجل التشغيلي نفسه.',
    solutionCopy: 'ابدأ بعمليات الوكالة، ثم ادع شركات العملاء إلى اعتمادات بسيطة ورؤية للفوترة. يدعم سجل التعيين والحضور والامتثال وجدول الوقت والتصدير نفسه فوترة الوكالة واعتماد الشركة دون بناء الحقيقة مرتين.',
  },
  comparison: {
    eyebrow: 'الطريقة القديمة مقابل رقيب 360',
    title: 'استبدل فوضى كل مورد بسير اعتماد وفوترة مشترك.',
    oldWayTitle: 'الطريقة القديمة',
    newWayTitle: 'طريقة رقيب 360',
    oldWay: [
      'جداول مختلفة لكل وكالة',
      'جداول وقت عبر البريد الإلكتروني',
      'اعتمادات عبر واتساب',
      'مطابقة فواتير يدوية',
      'تحضير رواتب يعاد بناؤه من ملفات منفصلة',
      'قوائم عمال متفرقة',
      'رؤية محدودة عبر المواقع',
      'مستندات امتثال مبعثرة في مجلدات',
      'عملية مختلفة لكل مورد',
    ],
    newWay: [
      'لوحة موحدة للقوى العاملة',
      'حضور وجداول وقت موحدة',
      'سير اعتماد منظم',
      'ضوابط رؤية بين الوكالة والشركة',
      'تحقق مشترك من الفوترة قبل نزاعات الفواتير',
      'سجل تعيينات العامل',
      'جاهزية المستندات والشهادات',
      'بيانات رواتب وتقارير جاهزة للتصدير',
      'عملية واحدة للقوى العاملة الداخلية والخارجية',
    ],
  },
  pricing: arabicPricingContent,
  featuresTitle: 'قدرات منصة القوى العاملة الجاهزة للبيع',
  features: [
    { title: 'تقليل العمل الإداري المتكرر', copy: 'استبدل التحقق اليدوي من الجداول وفحص الفوترة وملاحقة الاعتمادات وتحضير الرواتب بسير عمل واحد قابل للمراجعة.' },
    { title: 'تعاون العملاء بقيادة الوكالة', copy: 'ابدأ داخل فريق الوكالة، ثم ادع شركات العملاء إلى اعتمادات محددة ورؤية للفوترة عندما يصبح سير العمل جاهزا.' },
    { title: 'إدارة شبكة القوى العاملة', copy: 'أدر الموظفين والوكالات والمقاولين وموردي العمالة الخارجيين من منصة واحدة.' },
    { title: 'الحضور والنطاق الجغرافي', copy: 'تتبع حضور الموقع والتسجيل دون اتصال وامتثال الموقع والاستراحات والاستثناءات.' },
    { title: 'الجدولة والتعيينات', copy: 'أنشئ الوظائف والمناوبات والتعيينات والمناوبات المفتوحة وطلبات العمال عبر المواقع والمؤسسات.' },
    { title: 'الامتثال والمستندات', copy: 'تتبع مستندات العمال والشهادات ومخاطر الانتهاء ومتطلبات الاعتماد الخاصة بالوظائف.' },
    { title: 'الجداول والاعتمادات والتحقق من الفوترة', copy: 'وحّد اعتمادات المشرف والشركة مع سجل تدقيق وتعليقات ونزاعات وتعديلات ودعم أوضح للفواتير.' },
    { title: 'تصديرات الرواتب والتقارير', copy: 'أنشئ بيانات جاهزة للتصدير حسب العامل والوكالة والشركة والموقع والوظيفة وحالة الاعتماد ونطاق التاريخ.' },
    { title: 'رؤية الوكالات والمقاولين', copy: 'امنح كل مؤسسة مستوى الوصول المناسب دون كشف كل البيانات عبر المستأجرين.' },
    { title: 'تطور المهارات والموثوقية', copy: 'تتبع المهارات وحدد الفجوات وطابق العمال المؤهلين وادعم مسارات التقدم إلى جانب إشارات الموثوقية والحضور.' },
  ],
  useCasesTitle: 'مصمم للعمليات التي تعتمد على مصادر عمالة متعددة',
  useCases: [
    { title: 'مواقع المقاولات والمشاريع', copy: 'نسق العمالة المباشرة والمقاولين وتعيينات الموقع ومستندات الامتثال والحضور والاعتمادات والتصديرات.' },
    { title: 'إدارة المرافق', copy: 'وحّد التغطية والحضور عبر الهاتف ومراجعة المشرف وإغلاق الجداول عبر عقارات موزعة.' },
    { title: 'اللوجستيات والتوصيل', copy: 'أدر الفرق الميدانية والعمالة المؤقتة وتغطية المواقع واستثناءات المناوبات والتقارير الجاهزة للتصدير.' },
    { title: 'الضيافة والفعاليات', copy: 'اجمع الوكالات والعمال المؤقتين والمشرفين والجداول والاعتمادات في عملية جاهزة للفعاليات.' },
    { title: 'مقاولو الأمن والتنظيف', copy: 'تتبع تغطية النقاط وحضور الموقع ومستندات العمال واعتمادات المشرفين حسب موقع العميل.' },
    { title: 'وكالات التوظيف وتوريد العمالة', copy: 'ابدأ بعمليات الوكالة أولا، ثم أدخل العملاء إلى الاعتمادات المشتركة والتحقق من الفوترة ورؤية القوى العاملة.' },
    { title: 'عمليات متعددة المواقع', copy: 'امنح القادة رؤية عبر المواقع والمشاريع والوكالات والعمال والجداول والامتثال والاعتمادات والتصديرات.' },
  ],
  faqTitle: 'أسئلة يطرحها التنفيذيون وفرق التشغيل',
  faqEyebrow: 'الأسئلة الشائعة',
  faq: arabicFaq,
  finalTitle: 'ابدأ تقييم عمليات شبكة القوى العاملة لديك.',
  finalCopy: 'اجمع الطلب والتوفير والتوزيع والمستندات والحضور واعتماد الساعات في سجل تشغيل واحد بين الشركة والوكالة والمقاول.',
  finalCta: { label: 'ابدأ مجاناً', href: '/login?mode=register' },
  footer: {
    copyright: '© 2026 رقيب 360. جميع الحقوق محفوظة.',
    links: [
      { label: 'المنصة', href: '#platform' },
      { label: 'حالات الاستخدام', href: '#use-cases' },
      { label: 'التواصل', href: '#contact' },
    ],
  },
}

const frenchPricingContent: PricingContent = {
  ...pricingContent,
  eyebrow: 'Tarification NexusWorkforce360',
  title: 'Commencez gratuitement. Évoluez selon la capacité.',
  subtitle: 'Démarrez sur Free avec 10 utilisateurs et un superviseur/admin, dont six mois d’accès complet aux capacités essentielles éligibles. Passez à Starter, Growth ou Operations à mesure que vos effectifs grandissent. Punch AI, Predictive Forecasting, Punch Learning et Punch Mentor sont facturés séparément.',
  promo: 'Free : 10 utilisateurs + 1 superviseur. Six mois d’accès complet au cœur, puis mode Basic. Sans carte de crédit.',
  locationNote: 'Des dépassements par travailleur et par site s’appliquent au-delà de la capacité incluse de chaque forfait. Les protections d’usage raisonnable et de sécurité restent applicables.',
  includedLabel: 'Chaque forfait inclut :',
  bands: [
    { label: 'Free', price: '0 $', capacity: '10 utilisateurs + 1 superviseur · promotion cœur 6 mois · mode Basic ensuite' },
    { label: 'Starter', price: '1 000 $/mois', capacity: '100 travailleurs · 2 sites · opérations essentielles' },
    { label: 'Growth', price: '3 000 $/mois', capacity: '300 travailleurs · 5 sites · workflows et rapports enrichis' },
    { label: 'Operations', price: '8 000 $/mois', capacity: '1 000 travailleurs · 15 sites · gouvernance et audit avancés' },
  ],
  overageTitle: 'Dépassements et produits facturés séparément',
  overages: [
    { label: 'Travailleur actif supplémentaire', value: '10 $ (Starter) · 9 $ (Growth) · 8 $ (Operations) / travailleur / mois' },
    { label: 'Site supplémentaire', value: '150 $ / site / mois' },
    { label: 'Punch AI', value: 'À partir de 399 $ / mois (quota configuré)' },
    { label: 'Predictive Forecasting', value: 'À partir de 799 $ / mois · 5 sites inclus, puis 75 $ / site' },
    { label: 'Punch Learning', value: '6 $ / apprenant actif / mois · minimum 150 $' },
    { label: 'Punch Mentor', value: '12 $ / apprenant encadré / mois · minimum 250 $' },
    { label: 'Implémentation', value: 'Frais uniques, de 2 500 $ (Starter) à 10 000 $ (Operations)' },
  ],
  contactCta: { label: 'Contacter les ventes', href: '#contact' },
  implementationNote: 'Les déploiements Enterprise et Self-hosted sont proposés sur devis personnalisé. Les produits premium (Punch AI, Predictive Forecasting, Punch Learning, Punch Mentor) ne sont jamais inclus dans un forfait et s’achètent séparément. Implémentation, migration et formation sont des services ponctuels optionnels.',
  examples: [
    { exampleKey: 'small', name: 'Free', math: ['0 $ — 10 utilisateurs + 1 superviseur/admin', 'Six mois d’accès complet au cœur éligible', 'Continue automatiquement en mode Basic (aucune suppression)'], total: '0 $', annual: 'Sans carte de crédit. Produits premium vendus séparément.' },
    { exampleKey: 'mid-size', name: 'Starter', math: ['1 000 $ / mois de base', '100 travailleurs · 2 sites inclus', '+10 $ / travailleur · +150 $ / site'], total: '1 000 $/mois', annual: 'Opérations essentielles, planification, géofencing, feuilles de temps, exports paie.' },
    { exampleKey: 'professional', name: 'Growth', math: ['3 000 $ / mois de base', '300 travailleurs · 5 sites inclus', '+9 $ / travailleur · +150 $ / site'], total: '3 000 $/mois', annual: 'Tout Starter plus workflows inter-organisations et rapports enrichis.' },
    { exampleKey: 'larger', name: 'Operations', math: ['8 000 $ / mois de base', '1 000 travailleurs · 15 sites inclus', '+8 $ / travailleur · +150 $ / site'], total: '8 000 $/mois', annual: 'Tout Growth plus gouvernance avancée, audit et service prioritaire.' },
  ],
  features: [
    'Planification',
    'Présence mobile',
    'Géofencing',
    'Validations de feuilles de temps',
    'Documents travailleurs',
    'Exports paie',
    'Reporting',
    'Gestion des relations workforce',
  ],
}

const frenchContent: NexusLandingContent = {
  ...englishContent,
  nav: ['Économies', 'Network OS', 'Comparer', 'Tarifs', 'Écrans', 'Contact'],
  signIn: 'Se connecter',
  hero: {
    eyebrow: 'Le Workforce Network OS gratuit',
    title: 'Pilotez vos effectifs gratuitement',
    copy: 'Gérez travailleurs, jobs, plannings, sites, suivi du temps, géofencing, conformité, feuilles de temps et validations sur la plateforme essentielle à 0 $. Entreprises et agences de staffing partagent le même Workforce Network OS, sans tarif par travailleur ni par site pour le cœur.',
    primaryCta: { label: 'Commencer gratuitement', href: '/login?mode=register' },
    secondaryCta: { label: 'Voir comment ça marche', href: '#product' },
    proof: ['Opérations essentielles à 0 $', 'Aucun tarif par travailleur ou site', 'Entreprises, agences et travailleurs connectés'],
  },
  roi: {
    eyebrow: 'Estimation de planification',
    title: 'Estimez votre coût caché de coordination workforce',
    subtitle: 'La planification manuelle, les relances WhatsApp, les feuilles de temps papier, les contrôles de présence et les corrections paie consomment discrètement des heures chaque semaine.',
    inputs: {
      workers: 'Travailleurs actifs',
      locations: 'Sites',
      adminHours: 'Heures admin par semaine',
      adminRate: 'Coût horaire admin moyen',
      supervisorHours: 'Heures superviseur de validation par semaine',
    },
    results: {
      weekly: 'Coût hebdomadaire de coordination admin',
      monthly: 'Coût mensuel de coordination',
      annual: 'Coût annuel de coordination',
      reclaimed: 'Temps annuel potentiel récupéré avec 25 % de réduction',
    },
    assumptionsLabel: 'Hypothèses',
    formula: 'Formule : (heures admin + heures superviseur de validation) x coût horaire. Le mois utilise 4,33 semaines ; l’année utilise 52 semaines.',
    note: 'Les estimations reposent sur les hypothèses saisies par l’utilisateur et servent uniquement à la planification.',
    cta: { label: 'Voir comment NexusWorkforce360 réduit le travail de coordination', href: '#contact' },
    linksTitle: 'Pages liées aux opérations workforce',
    links: [
      { label: 'Logiciel de gestion workforce construction', href: '/construction-workforce-management-software' },
      { label: 'Logiciel feuilles de temps agences staffing', href: '/staffing-agency-timesheet-software' },
      { label: 'Logiciel agence staffing entrepôt', href: '/warehouse-staffing-agency-software' },
      { label: 'Logiciel conformité workforce construction', href: '/construction-workforce-compliance-software' },
    ],
  },
  problemSolution: {
    eyebrow: 'Problème et solution',
    problemTitle: 'La validation manuelle transforme chaque clôture paie en friction back-office.',
    problemCopy: 'Les agences rapprochent plannings, heures, feuilles de temps, tarifs, validations client, questions facture et préparation paie dans des chats et tableurs. Les entreprises perdent du temps à approuver les heures, contrôler les factures, résoudre les litiges de présence et suivre la main-d’œuvre par site.',
    solutionTitle: 'NexusWorkforce360 aligne agence et client autour des mêmes données vérifiées.',
    solutionCopy: 'Commencez avec l’équipe agence, puis invitez les clients dans des validations simples et une visibilité facturation. Le même enregistrement d’affectation, présence, conformité, feuille de temps et export soutient la facturation agence et l’approbation client.',
  },
  comparison: {
    eyebrow: 'Ancienne méthode vs NexusWorkforce360',
    title: 'Remplacez le chaos fournisseur par fournisseur par un workflow partagé de validation et facturation.',
    oldWayTitle: 'Ancienne méthode',
    newWayTitle: 'Méthode NexusWorkforce360',
    oldWay: [
      'Tableurs propres à chaque agence',
      'Feuilles de temps par e-mail',
      'Validations WhatsApp',
      'Rapprochement manuel des factures',
      'Préparation paie reconstruite depuis des fichiers séparés',
      'Listes travailleurs séparées',
      'Visibilité limitée entre sites',
      'Documents conformité dispersés',
    ],
    newWay: [
      'Tableau workforce unifié',
      'Présence et feuilles de temps standardisées',
      'Workflows de validation structurés',
      'Contrôles de visibilité agence/client',
      'Validation facturation partagée avant litige',
      'Historique d’affectation travailleur',
      'Readiness documents et certifications',
      'Données paie et reporting prêtes à l’export',
    ],
  },
  pricing: frenchPricingContent,
  featuresTitle: 'Capacités plateforme prêtes pour la vente',
  features: [
    { title: 'Économies de temps administratif', copy: 'Économisez 4 à 5 heures par semaine en remplaçant validation manuelle, contrôles facture, relances d’approbation et préparation paie par un workflow partagé.' },
    { title: 'Collaboration agence-client', copy: 'Démarrez dans l’équipe agence, puis invitez les clients dans des validations et une visibilité facturation à accès limité.' },
    { title: 'Workforce Network Management', copy: 'Gérez employés, agences, sous-traitants et fournisseurs de main-d’œuvre depuis une seule plateforme.' },
    { title: 'Présence et géofencing', copy: 'Suivez présence site, pointages hors-ligne, conformité localisation, pauses et exceptions.' },
    { title: 'Planification et affectations', copy: 'Créez jobs, shifts, affectations, shifts ouverts et candidatures travailleurs entre sites et organisations.' },
    { title: 'Conformité et documents', copy: 'Suivez documents, certifications, risques d’expiration et exigences par poste.' },
    { title: 'Feuilles de temps, validations et facturation', copy: 'Standardisez les validations superviseur et client avec historique, commentaires, litiges, ajustements et support facture.' },
    { title: 'Progression des compétences et fiabilité', copy: 'Suivez les compétences, identifiez les écarts, associez les travailleurs qualifiés et soutenez des parcours d’avancement.' },
  ],
  useCasesTitle: 'Conçu pour les opérations à sources de main-d’œuvre multiples',
  useCases: [
    { title: 'Chantiers construction', copy: 'Coordonnez main-d’œuvre directe, sous-traitants, affectations site, documents, présence, validations et exports.' },
    { title: 'Gestion facilities', copy: 'Standardisez couverture, présence mobile, validation superviseur et clôture feuilles de temps sur des propriétés distribuées.' },
    { title: 'Hospitality et événements', copy: 'Réunissez agences, temporaires, superviseurs, plannings et validations dans un processus prêt pour l’événement.' },
    { title: 'Sécurité et nettoyage', copy: 'Suivez couverture des postes, présence par site, documents travailleurs et validations superviseur par site client.' },
    { title: 'Agences staffing et manpower', copy: 'Lancez d’abord les opérations agence, puis ajoutez les clients aux validations, contrôles facture et visibilité workforce.' },
    { title: 'Opérations multi-sites', copy: 'Donnez aux leaders une visibilité par sites, projets, agences, travailleurs, feuilles de temps, conformité, validations et exports.' },
  ],
  faqTitle: 'Questions ventes des dirigeants et opérateurs',
  faqEyebrow: 'FAQ',
  faq: frenchFaq,
  finalTitle: 'Réduisez l’administration workforce de 260+ heures par an.',
  finalCopy: 'Réunissez plannings, présence, validations, feuilles de temps, contrôle facturation, conformité, progression des compétences et exports paie dans un seul enregistrement agence-client.',
  finalCta: { label: 'Commencer gratuitement', href: '/login?mode=register' },
  footer: {
    copyright: '© 2026 NexusWorkforce360. Tous droits réservés.',
    links: [
      { label: 'Plateforme', href: '#platform' },
      { label: 'Cas d’usage', href: '#use-cases' },
      { label: 'Contact', href: '#contact' },
    ],
  },
}

export const nexusLandingContent: Record<LandingLocale, NexusLandingContent> = {
  en: englishContent,
  ar: arabicContent,
  tr: englishContent,
  fr: frenchContent,
}
